import { useMemo } from "react"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import linkIcon from "../../../assets/link.svg"
import cubeIcon from "../../../assets/cube.svg"
import { BASE_ROUTE } from "../../../routes"
import { SemanticHeader } from "./SemanticHeader"
import Typography, { typographyTypes } from "./Typography"
import Button from "./Button"

interface ErrorMessageProps {
  clickHandlerOverride?: () => void
  sessionIdMessage?: string
  textOverrides?: { header?: string; content?: string; buttonText?: string }
  type?: "notFound" | "general" | "underConstruction"
}

export const ErrorMessage = ({
  clickHandlerOverride,
  sessionIdMessage,
  textOverrides,
  type = "general",
}: ErrorMessageProps) => {
  const { cms: cmsContent } = useCMSReady(["common", "systemMessages"])
  const { common, systemMessages } = cmsContent

  const headerText = useMemo(() => {
    if (textOverrides?.header) {
      return textOverrides.header
    }
    switch (type) {
      case "notFound":
        return systemMessages?.pageNotFoundHeader || "Page Not Found"
      case "underConstruction":
        return systemMessages?.underConstructionHeader || "Under Construction"
      case "general":
      default:
        return (
          systemMessages?.generalErrorHeader || "We've Encountered an Error"
        )
    }
  }, [
    systemMessages?.generalErrorHeader,
    systemMessages?.pageNotFoundHeader,
    systemMessages?.underConstructionHeader,
    textOverrides?.header,
    type,
  ])

  const contentText = useMemo(() => {
    if (textOverrides?.content) {
      return textOverrides.content
    }
    switch (type) {
      case "notFound":
        return (
          systemMessages?.notFoundMessageContent ||
          "The page you're looking for isn't here. It may have moved or no longer exists."
        )
      case "underConstruction":
        return (
          systemMessages?.underConstructionMessageContent ||
          "This page is in active development."
        )
      case "general":
      default:
        return (
          systemMessages?.generalErrorMessageContent ||
          "Our apologies. We've encountered an issue. Please reload/refresh the page."
        )
    }
  }, [
    systemMessages?.generalErrorMessageContent,
    systemMessages?.notFoundMessageContent,
    systemMessages?.underConstructionMessageContent,
    textOverrides?.content,
    type,
  ])

  const buttonText = useMemo(() => {
    if (textOverrides?.buttonText) {
      return textOverrides.buttonText
    }
    switch (type) {
      case "notFound":
      case "underConstruction":
        return common?.goBack || "Go Back"
      case "general":
      default:
        return common?.refreshPage || "Refresh Page"
    }
  }, [common?.goBack, common?.refreshPage, textOverrides?.buttonText, type])

  const clickHandler = useMemo(() => {
    if (clickHandlerOverride) {
      return clickHandlerOverride
    }

    switch (type) {
      case "notFound":
      case "underConstruction":
        return () => window.history.back()
      case "general":
      default:
        return () => (location.href = BASE_ROUTE)
    }
  }, [clickHandlerOverride, type])

  const systemMessageIcon = useMemo(() => {
    switch (type) {
      case "notFound":
        return linkIcon
      case "underConstruction":
        return cubeIcon
      case "general":
      default:
        return null
    }
  }, [type])

  return (
    <div className="flex justify-center pt-9">
      <div
        role="alert"
        className="flex max-w-[590px] bg-white rounded-lg p-6 shadow-md"
      >
        {systemMessageIcon && (
          <img
            src={systemMessageIcon}
            alt="system message icon"
            className="self-start h-[48px] w-[48px] mr-2"
          />
        )}
        <div
          role="alert"
          className="flex flex-col justify-center max-w-[590px]"
        >
          <SemanticHeader
            text={headerText}
            type="xlHeader"
            headerLevel="h1"
            styleOverrides="!text-7.5 !leading-11 mb-1 mt-0"
          />
          <Typography
            variant={typographyTypes.NORMAL}
            styleOverrides="font-medium text-base max-w-[368px] text-gray-500 !leading-[30px]"
            text={contentText}
          />
          {sessionIdMessage && (
            <Typography
              styleOverrides="pt-4 text-gray-500"
              variant={typographyTypes.NORMAL}
              text={sessionIdMessage}
            />
          )}
          <Button
            buttonLabel={buttonText}
            roundedFull={false}
            onClick={clickHandler}
            styleOverrides="mt-6"
          />
        </div>
      </div>
    </div>
  )
}
